<script lang="ts">
	import { getContext, type SvelteComponent } from "svelte";
	import { RenderMode } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import type { Component, Page } from "#gen/pages/v2/page_pb.js";
	import DynamicComponent from "#lib/components/page/DynamicComponent.svelte";
	import { isFormComponentDef, isTopLevelPageVboxContainer } from "#lib/components/page/editing/utils.js";
	import type { ComponentDefMap, Registry } from "#lib/components/page/registry.js";
	import type { JsonObject } from "#lib/protobuf-compat.js";
	import { objectValue, stringValue, structValue } from "#lib/struct.js";
	import { cssStringify } from "#lib/style.js";
	import type { Readable } from "svelte/store";

	export interface DynamicComponentProps {
		id: string;
		component: Component;
		definitions: ComponentDefMap;
		mode: Mode;
		page: Readable<Page>;
		data: Readable<JsonObject | undefined>;
		result: Readable<JsonObject | undefined>;
		readOnly: boolean;
		renderMode: RenderMode;
		parentDirection?: string;
	}

	type ViewerInstance = SvelteComponent & { value?: (out: JsonObject) => void; validate?: () => Promise<boolean> };

	const registry: Registry = getContext("registry");

	let { id, component, definitions, mode = Mode.LIVE, page, data, result, readOnly, renderMode = RenderMode.MODAL, parentDirection }: DynamicComponentProps = $props();

	const isContainer = $derived(definitions[component.typeUri]?.isContainer ?? false);
	const fillsVertical = $derived(isFormComponentDef(component.typeUri, definitions) || isTopLevelPageVboxContainer(id, $page));
	const containerDirection = $derived(stringValue(structValue($page.config, component.id), "direction"));
	const sizeToContentWidth = $derived(!isContainer && !fillsVertical && parentDirection === "col");

	const connectable = $derived(definitions[component.typeUri]?.isConnectable ?? false);
	const connectionRequired = $derived(definitions[component.typeUri]?.connectionRequired ?? false);

	const Viewer = $derived(component && (registry.getComponent(definitions[component.typeUri]) as unknown as new (...args: unknown[]) => ViewerInstance));
	const style: string | undefined = $derived(
		component
			? cssStringify({
					...(definitions[component.typeUri]?.defaultSytyle as JsonObject),
					...(component.style as JsonObject),
					...styleFromConfig(),
				})
			: undefined,
	);

	let viewer: ViewerInstance | null = $state(null);
	let nested: Record<string, DynamicComponent> = $derived({});

	function setValue(out: JsonObject, component: DynamicComponent | ViewerInstance) {
		if (!component?.value) {
			return;
		}
		component.value(out);
	}

	export function value(out: JsonObject) {
		if (viewer) {
			setValue(out, viewer);
		}
		Object.keys(nested).forEach(key => {
			setValue(out, nested[key]);
		});
	}

	export async function validate(): Promise<boolean> {
		let valid: boolean = viewer?.validate ? await viewer.validate() : true;
		for (const key in nested) {
			const component = nested[key];
			if (component && component.validate) {
				valid = (await component.validate()) && valid;
			}
		}
		return valid;
	}

	function styleFromConfig(): JsonObject {
		const config = objectValue($page.config, id);
		if (!config) {
			return {};
		}
		return config.style as JsonObject;
	}
</script>

{#if component && Viewer}
	{#if isContainer}
		<Viewer bind:this={viewer} id={component.id} {style} {page} {data} {result} {readOnly} {renderMode} {mode} typeUri={component.typeUri} {connectable} {connectionRequired}>
			{#each component.children ?? [] as child (child.id)}
				<DynamicComponent bind:this={nested[child.id]} id={child.id} component={child} {definitions} {page} {data} {result} {readOnly} {renderMode} {mode} parentDirection={containerDirection} />
			{/each}
		</Viewer>
	{:else if sizeToContentWidth}
		<div class="w-auto shrink-0">
			<Viewer bind:this={viewer} id={component.id} {style} {page} {data} {result} {readOnly} {renderMode} {mode} typeUri={component.typeUri} {connectable} {connectionRequired} />
		</div>
	{:else}
		<Viewer bind:this={viewer} id={component.id} {style} {page} {data} {result} {readOnly} {renderMode} {mode} typeUri={component.typeUri} {connectable} {connectionRequired} />
	{/if}
{/if}
